import React, { useState, useRef, useEffect } from 'react';
import html2canvas from 'html2canvas';
import { reportBug, generateBugDescription } from '../../api/bugApi';

const BugReportModal = ({ isOpen, onClose, currentUrl }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [characterCount, setCharacterCount] = useState(0);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState('');
  const [loadingStatusIndex, setLoadingStatusIndex] = useState(0);
  
  const modalRef = useRef(null);
  const titleInputRef = useRef(null);
  const statusTimeoutRef = useRef(null);

  // Status messages array with text and icon
  const statusMessages = [
    { text: 'Uploading Screenshot', icon: '📸' },
    { text: 'Pending', icon: '⏳' },
    { text: 'Parsing Image', icon: '🔍' }
  ];

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
      }
    };
  }, []);

  // Focus on title input when modal opens
  useEffect(() => {
    if (isOpen && titleInputRef.current) {
      setTimeout(() => {
        titleInputRef.current.focus();
      }, 100);
    }
  }, [isOpen]);

  // Reset modal visibility when opened
  useEffect(() => {
    if (isOpen) {
      setIsModalVisible(true);
    }
  }, [isOpen]);

  // Close modal when clicking outside and handle escape key
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'description') {
      setCharacterCount(value.length);
    }
  };

  const captureScreenshot = async () => {
    try {
      // Temporarily hide the modal to ensure it's not in the screenshot
      setIsModalVisible(false);
      
      // Small delay to ensure modal is hidden before capture
      await new Promise(resolve => setTimeout(resolve, 50));
      
      const canvas = await html2canvas(document.body, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
        allowTaint: false,
        windowWidth: document.documentElement.scrollWidth,
        windowHeight: document.documentElement.scrollHeight,
      });
      
      const blob = await new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'image/png', 1.0);
      });
      
      // Restore modal visibility
      setIsModalVisible(true);
      
      return new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' });
      
    } catch (err) {
      console.error('Failed to capture screenshot:', err);
      // Restore modal visibility on error
      setIsModalVisible(true);
      throw new Error('Failed to capture screenshot');
    }
  };

  const generateDescriptionWithAI = async () => {
    if (!formData.title.trim()) {
      setError('Please enter a title first to generate description');
      return;
    }

    setIsGeneratingDescription(true);
    setError('');

    try {
      const response = await generateBugDescription({
        title: formData.title
      });
      
      const generatedDescription = response.description || 
                                   response.data || 
                                   response.result ||
                                   response;
      
      if (generatedDescription && typeof generatedDescription === 'string') {
        setFormData(prev => ({ 
          ...prev, 
          description: generatedDescription 
        }));
        setCharacterCount(generatedDescription.length);
        setSuccess('✨ AI description generated successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.message || 'Failed to generate description');
      }
    } catch (err) {
      console.error('Failed to generate description:', err);
      setError(err.response?.data?.message || 'Failed to generate description. Please try again.');
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  // Start dynamic status updates (non-looping with 3.5 second intervals)
  const startDynamicStatus = () => {
    setLoadingStatusIndex(0);
    setLoadingStatus(statusMessages[0].text);
    
    // Clear any existing timeout
    if (statusTimeoutRef.current) {
      clearTimeout(statusTimeoutRef.current);
    }
    
    // Function to update to next status
    const updateToNextStatus = (currentIndex) => {
      if (currentIndex + 1 < statusMessages.length) {
        const nextIndex = currentIndex + 1;
        setLoadingStatusIndex(nextIndex);
        setLoadingStatus(statusMessages[nextIndex].text);
        
        // Schedule next update after 3.5 seconds if not at last status
        if (nextIndex + 1 < statusMessages.length) {
          statusTimeoutRef.current = setTimeout(() => {
            updateToNextStatus(nextIndex);
          }, 3500);
        } else {
          // At last status (Parsing Image), clear timeout reference
          statusTimeoutRef.current = null;
        }
      }
    };
    
    // Start the sequence - first update after 3.5 seconds
    statusTimeoutRef.current = setTimeout(() => {
      updateToNextStatus(0);
    }, 3500);
  };

  // Stop dynamic status updates
  const stopDynamicStatus = () => {
    if (statusTimeoutRef.current) {
      clearTimeout(statusTimeoutRef.current);
      statusTimeoutRef.current = null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.title.trim()) {
      setError('Please enter a title for the bug');
      return;
    }
    
    if (!formData.description.trim()) {
      setError('Please enter a description or generate one using AI');
      return;
    }

    setIsSubmitting(true);

    try {
      // First, capture screenshot WITHOUT showing any overlay
      // This ensures the screenshot doesn't include the loading popup
      let screenshotFile = null;
      try {
        screenshotFile = await captureScreenshot();
      } catch (screenshotError) {
        console.error('Screenshot failed:', screenshotError);
        setIsSubmitting(false);
        setError('Failed to capture screenshot. Please try again.');
        return;
      }

      // Now that screenshot is captured, show loading overlay with dynamic status
      // Hide the modal with animation
      setIsModalVisible(false);
      
      // Wait for modal fade out animation
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Show loading overlay and start dynamic status
      setShowLoadingOverlay(true);
      startDynamicStatus();
      
      // Small delay to ensure loading overlay is visible
      await new Promise(resolve => setTimeout(resolve, 150));

      // Submit the bug report (keep loading overlay visible during API call)
      const response = await reportBug({
        title: formData.title,
        description: formData.description,
        url: currentUrl,
        screenshot: screenshotFile
      });

      console.log('API Response:', response);

      // Stop dynamic status updates
      stopDynamicStatus();
      
      // Hide loading overlay after API call completes
      setShowLoadingOverlay(false);

      // Small pause before showing success
      await new Promise(resolve => setTimeout(resolve, 150));

      // Check for success — handles common API response shapes
      const isSuccess =
        response?.success === true ||
        response?.status === 'success' ||
        response?.status === 200 ||
        response?.id ||
        response?.data?.id ||
        response?.message?.toLowerCase().includes('success');

      if (isSuccess) {
        setShowSuccessOverlay(true);

        // Auto-close after showing success message
        setTimeout(() => {
          setShowSuccessOverlay(false);
          resetForm();
          setIsModalVisible(true);
          onClose();
        }, 3000);
      } else {
        // API returned but indicated failure
        setIsModalVisible(true);
        setError(response?.message || 'Failed to submit bug report. Please try again.');
      }
      
    } catch (err) {
      console.error('Failed to submit bug report:', err);
      stopDynamicStatus();
      setShowLoadingOverlay(false);
      setIsModalVisible(true);
      
      if (err.response?.status === 401) {
        setError('Authentication required. Please log in again.');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to report bugs.');
      } else {
        setError(err.response?.data?.message || 'Failed to submit bug report. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '' });
    setCharacterCount(0);
    setError('');
    setSuccess('');
    stopDynamicStatus(); // Clean up status timeout on reset
  };

  const handleClose = () => {
    if (isSubmitting) return;
    resetForm();
    setIsModalVisible(true);
    onClose();
  };

  if (!isOpen) return null;

  // Get current icon based on status
  const getCurrentIcon = () => {
    const currentMessage = statusMessages.find(msg => msg.text === loadingStatus);
    return currentMessage ? currentMessage.icon : statusMessages[0].icon;
  };

  // Calculate progress percentage (non-looping, based on current index)
  const getProgressPercentage = () => {
    // Progress increases with each status, max at 100% when on last status
    return ((loadingStatusIndex + 1) / statusMessages.length) * 100;
  };

  return (
    <>
      {/* Success Overlay - Highest Priority */}
      {showSuccessOverlay && (
        <div 
          className="fixed inset-0 z-[999999] flex items-center justify-center p-4" 
          style={{ 
            backgroundColor: 'rgba(0, 0, 0, 0.8)', 
            backdropFilter: 'blur(8px)'
          }}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all duration-500 animate-success-in">
            <div className="p-8 text-center">
              {/* Success Animation */}
              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-24 h-24 bg-green-100 rounded-full animate-ping opacity-75"></div>
                </div>
                <div className="relative w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto shadow-lg animate-bounce-in">
                  <svg className="h-12 w-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Bug Reported Successfully!</h3>
              <p className="text-gray-600 mb-4">Thank you for helping improve Operis.</p>
              
              <div className="bg-green-50 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-center space-x-2">
                  <svg className="h-5 w-5 text-green-600 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-green-700 font-medium">Our team will review it shortly</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay with Dynamic Status */}
      {showLoadingOverlay && (
        <div 
          className="fixed inset-0 z-[999998] flex items-center justify-center p-4" 
          style={{ 
            backgroundColor: 'rgba(0, 0, 0, 0.7)', 
            backdropFilter: 'blur(4px)'
          }}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full transform transition-all duration-500 animate-scale-in">
            <div className="p-8 text-center">
              <div className="relative mb-6">
                <div className="animate-spin rounded-full h-20 w-20 border-4 border-red-200 border-t-red-600 mx-auto"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-3xl animate-pulse">{getCurrentIcon()}</span>
                </div>
              </div>
              
              {/* Dynamic Status Text */}
              <h4 className="text-lg font-semibold text-gray-800 mb-2 transition-all duration-300">
                {loadingStatus || 'Uploading Screenshot'}
              </h4>
              
              <p className="text-sm text-gray-600">
                Please wait while we process your bug report...
              </p>
              
              <div className="mt-4 flex justify-center space-x-1">
                <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
              </div>
              
              {/* Progress Indicator - Non-looping */}
              <div className="mt-4 w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-red-500 to-red-600 h-full rounded-full transition-all duration-500 ease-out"
                  style={{ 
                    width: `${getProgressPercentage()}%`,
                    transition: 'width 0.5s ease-out'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Backdrop */}
      <div 
        className={`fixed inset-0 transition-all duration-300 ease-out ${isModalVisible ? 'opacity-100' : 'opacity-0'}`}
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
          zIndex: 99999,
          transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          pointerEvents: isModalVisible ? 'auto' : 'none'
        }}
        onClick={handleClose}
      />
      
      {/* Modal Container */}
      <div 
        className={`fixed inset-0 overflow-y-auto transition-all duration-300 ease-out ${isModalVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4'}`}
        style={{ 
          zIndex: 100000,
          transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          pointerEvents: isModalVisible ? 'auto' : 'none'
        }}
      >
        <div className="flex min-h-full items-center justify-center p-4">
          <div 
            ref={modalRef}
            className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full transform transition-all duration-300"
          >
            {/* Header Section */}
            <div className="relative overflow-hidden bg-gradient-to-r from-gray-50 to-white rounded-t-2xl">
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-red-50 to-orange-50 rounded-full opacity-50 blur-3xl -mr-32 -mt-32" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-red-50 to-orange-50 rounded-full opacity-50 blur-3xl -ml-24 -mb-24" />
              
              <div className="relative px-6 pt-6 pb-5">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h2 className="text-3xl font-extrabold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
                      Report a Bug
                    </h2>
                    <div className="mt-2 flex items-center space-x-2">
                      <div className="h-0.5 w-8 bg-gradient-to-r from-red-500 to-red-600 rounded-full"></div>
                      <p className="text-sm text-gray-600">
                        Help us improve <span className="font-semibold text-gray-800">Operis</span> by reporting any issues you encounter
                      </p>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        <svg className="h-3 w-3 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Auto-screenshot
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        <svg className="h-3 w-3 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        AI-Powered
                      </span>
                    </div>
                  </div>
                  
                  <button
                    onClick={handleClose}
                    disabled={isSubmitting}
                    className="relative group p-2 rounded-xl hover:bg-gray-100 transition-all duration-200 disabled:opacity-50"
                    aria-label="Close"
                  >
                    <svg className="h-5 w-5 text-gray-400 group-hover:text-gray-600 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              {/* Success Message */}
              {success && (
                <div className="mb-5 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg animate-slide-in">
                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <p className="ml-3 text-sm font-medium text-green-800">{success}</p>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="mb-5 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg animate-shake">
                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <p className="ml-3 text-sm font-medium text-red-800">{error}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* Title Field */}
                <div className="mb-5">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                      </svg>
                    </div>
                    <input
                      ref={titleInputRef}
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      disabled={isSubmitting}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed"
                      placeholder="Brief description of the bug"
                      autoComplete="off"
                    />
                  </div>
                </div>

                {/* Description Field with AI Button */}
                <div className="mb-5">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={generateDescriptionWithAI}
                      disabled={isGeneratingDescription || !formData.title.trim() || isSubmitting}
                      className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center space-x-1"
                    >
                      {isGeneratingDescription ? (
                        <>
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Generating...</span>
                        </>
                      ) : (
                        <>
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                          <span>AI Generate Description</span>
                        </>
                      )}
                    </button>
                  </div>
                  <div className="relative">
                    <div className="absolute top-3 left-3 pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                      </svg>
                    </div>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      maxLength={500}
                      disabled={isSubmitting}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed resize-none"
                      placeholder="Please describe what happened, steps to reproduce, and expected behavior... or click 'AI Generate Description' above to auto-generate"
                    />
                    <div className="absolute bottom-2 right-3 text-xs text-gray-400">
                      {characterCount}/500
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    ✨ AI will generate a detailed description based on your title
                  </p>
                </div>

                {/* URL Info Card */}
                <div className="mb-5 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.102m-4.242-4.242l4 4m0-8l4-4" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Current Page URL</p>
                      <p className="text-sm text-gray-700 mt-1 break-all font-mono">{currentUrl}</p>
                    </div>
                  </div>
                </div>

                {/* Screenshot Info Card */}
                <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                        <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-blue-900">Automatic Screenshot Capture</p>
                      <p className="text-xs text-blue-700 mt-1">
                        A screenshot of the current page will be automatically captured and attached when you submit this report.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isSubmitting}
                    className="px-5 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 transition-all duration-200 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:from-red-700 hover:to-red-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium shadow-md hover:shadow-lg flex items-center space-x-2"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <span>Report Bug</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        @keyframes bounceIn {
          0% { opacity: 0; transform: scale(0.3); }
          50% { opacity: 1; transform: scale(1.05); }
          70% { transform: scale(0.9); }
          100% { transform: scale(1); }
        }
        @keyframes successIn {
          from { opacity: 0; transform: scale(0.9) translateY(20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        .animate-success-in { animation: successIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1); }
        .animate-bounce-in { animation: bounceIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55); }
        .animate-scale-in { animation: scaleIn 0.3s ease-out; }
        .animate-slide-in { animation: slideIn 0.3s ease-out; }
        .animate-shake { animation: shake 0.3s ease-in-out; }
        .animate-bounce { animation: bounce 1s infinite; }
      `}</style>
    </>
  );
};

export default BugReportModal;